import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const TOKEN_KEY = 'access_token';
const USER_KEY = 'user';

// Global axios response interceptor — if any request comes back 401 with the
// "Session expired" detail (set by the backend when token_version is stale),
// clear local auth and bounce the user to /login.
let interceptorInstalled = false;
function installAuthInterceptor() {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    (resp) => resp,
    (error) => {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || '';
      const isSessionExpired = status === 401 && typeof detail === 'string' && /session expired/i.test(detail);
      if (isSessionExpired) {
        try {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        } catch (_) { /* ignore */ }
        // Hard-redirect so all React state resets cleanly.
        if (typeof window !== 'undefined' && !/\/login(\?|$)/.test(window.location.pathname + window.location.search)) {
          const next = window.location.pathname + window.location.search;
          window.location.assign(`/login?next=${encodeURIComponent(next)}&reason=session_expired`);
        }
      }
      return Promise.reject(error);
    }
  );
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { installAuthInterceptor(); }, []);

  const setSession = useCallback((token, userObj) => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(userObj));
      setUser(userObj);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
    }
  }, []);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(data);
      localStorage.setItem(USER_KEY, JSON.stringify(data));
    } catch (e) {
      setSession(null, null);
    } finally {
      setLoading(false);
    }
  }, [setSession]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setSession(data.access_token, data.user);
    return data.user;
  };

  const register = async ({ email, password, first_name, last_name }) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, first_name, last_name });
    setSession(data.access_token, data.user);
    return data.user;
  };

  const logout = () => {
    setSession(null, null);
  };

  // Called by ChangePasswordForm with the fresh token issued by the server
  // so the user's current tab stays signed in (other tabs/devices get bounced).
  const updateToken = useCallback((token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
  }, []);

  const refresh = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, login, register, logout, refresh, authHeader, updateToken }}>
      {children}
    </AuthContext.Provider>
  );
};
